// ============================================================================
// DIRECTOR SIGNATORY MASTER SCREEN — FSA Signature Workflow
// ============================================================================
// Lists fsa.tbl_director_signatory. Per-client roster — the engagement
// signature picker filters by client_id + the engagement's report_date
// within effective window.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useFsaDirectorSignatories } from 'hooks/api/fsa/useFsaSignature';
import { fsaDirectorSignatoryService } from 'api/services/fsa/fsaSignatureService';
import { clientService } from 'api/services/clientService';
import type { DirectorSignatory, DirectorSignatorySearchRequest } from 'api/types/fsa/signature.types';
import DirectorSignatoryFormModal from 'components/modals/modules/fsa/DirectorSignatoryFormModal';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

const FsaDirectorSignatoryMasterScreen: React.FC = () => {
  const [queryParams, setQueryParams] = useState<DirectorSignatorySearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useFsaDirectorSignatories(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState<string>('');
  const [nameFilter, setNameFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      clientId:        clientFilter || undefined,
      signatoryName:   nameFilter || undefined,
      designation:     designationFilter || undefined,
      statusCode:      statusFilter || undefined,
      includeInactive: statusFilter === 'INACTIVE',
      page:            1,
    }));
    setFilterOpen(false);
  }, [clientFilter, nameFilter, designationFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setClientFilter('');
    setNameFilter('');
    setDesignationFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({
      ...prev,
      clientId: undefined, signatoryName: undefined, designation: undefined, statusCode: undefined, includeInactive: undefined, page: 1,
    }));
  }, []);

  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    return [{ value: '', label: 'All clients' }, ...resp.data.map(c => ({
      value: String(c.id),
      label: c.legalName ? `${c.legalName} (${c.code})` : `${c.name} (${c.code})`,
    }))];
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedSignatory, setSelectedSignatory] = useState<DirectorSignatory | null>(null);

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns: SyutGridColumn<DirectorSignatory>[] = useMemo(() => [
    { id: 'clientLegalName', header: 'Client', accessorKey: 'clientLegalName', enableSorting: true, size: 240 },
    { id: 'signatoryName', header: 'Signatory', accessorKey: 'signatoryName', enableSorting: true, size: 200 },
    { id: 'designation', header: 'Designation', accessorKey: 'designation', enableSorting: true, size: 160 },
    { id: 'din', header: 'DIN', accessorKey: 'din', enableSorting: false, size: 110 },
    { id: 'effectiveFrom', header: 'Effective From',
      accessorFn: (row: DirectorSignatory) => row.effectiveFrom,
      cell: ({ row }: { row: { original: DirectorSignatory } }) => <span>{formatDate(row.original.effectiveFrom)}</span>,
      size: 120, enableSorting: true },
    { id: 'effectiveTo', header: 'Effective To',
      accessorFn: (row: DirectorSignatory) => row.effectiveTo,
      cell: ({ row }: { row: { original: DirectorSignatory } }) => (
        <span style={{ color: row.original.effectiveTo ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {row.original.effectiveTo ? formatDate(row.original.effectiveTo) : 'Active'}
        </span>
      ),
      size: 120, enableSorting: true },
    { id: 'status', header: 'Status', accessorKey: 'statusName', enableSorting: true, size: 100 },
  ], []);

  const handleEdit = useCallback((row: DirectorSignatory) => {
    setSelectedSignatory(row);
    setFormMode('edit');
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((row: DirectorSignatory) => {
    setSelectedSignatory(row);
    setDeleteModalOpen(true);
  }, []);

  const getRowActions = useCallback((row: DirectorSignatory): ActionMenuItem[] => [
    { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={16} />, onClick: () => handleEdit(row) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(row), variant: 'danger' },
  ], [handleEdit, handleDeleteClick]);

  const handleAdd = useCallback(() => {
    setSelectedSignatory(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add',     icon: <IconSystem.add     size={16} />, label: 'Add',     onClick: handleAdd, variant: 'primary' },
    { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => refetch(), variant: 'icon' },
  ], [handleAdd, refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedSignatory) return;
    setIsDeleting(true);
    try {
      await fsaDirectorSignatoryService.remove(selectedSignatory.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedSignatory.signatoryName}" deleted successfully.`);
      setSelectedSignatory(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Delete failed.', 6000);
    }
  }, [selectedSignatory, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Directors / Signatories"
        description="Per-client roster of directors and officers. Used by FSA Signature Workflow to populate the board side of signed Financial Statements."
        icon={<IconSystem.users size={20} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search signatories…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={getRowActions}
              emptyMessage={loading ? 'Loading…' : 'No directors / signatories found'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Client"
                  value={clientFilter}
                  onChange={(val) => setClientFilter((val as string) || '')}
                  isAsync
                  loadOptions={loadClientOptions}
                  defaultOptions
                  cacheOptions={false}
                  isClearable
                  size="sm"
                  placeholder="All clients"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Signatory Name</label>
                <input type="text" className="form-input" value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Designation</label>
                <input type="text" className="form-input" value={designationFilter}
                  onChange={(e) => setDesignationFilter(e.target.value)}
                  placeholder="Filter by designation…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete signatory "${selectedSignatory?.signatoryName}"? Existing engagement signature panels that reference this signatory will keep their snapshot data, but new engagements won't be able to select them.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <DirectorSignatoryFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedSignatory}
        onSaved={refetch}
        onClose={() => setFormModalOpen(false)}
      />
    </div>
  );
};

export default FsaDirectorSignatoryMasterScreen;
